import React, { forwardRef, useImperativeHandle } from 'react';
import { FormBuilder, FormSchema, FormBuilderRef } from '../../../components/formBuilder';
import { useTranslation } from 'react-i18next';
import { NoDriverReason, Reservation } from '../../../models/reservation';
import { enumToOptions } from '../../../utils/misc';
import { Driver } from '../../../models/driver';
import { Vehicle } from '../../../models/vehicle';

const vehicleLabel = (v: Vehicle & Record<string, unknown>) => {
  const truck = v.truckPlate ?? v.truck_plate ?? '';
  const trailer = v.trailerPlate ?? v.trailer_plate ?? '';
  const plate = `${truck}${trailer ? ` / ${trailer}` : ''}`;
  const name = v.name ? ` (${v.name})` : '';
  return `${plate}${name}`.trim() || `#${v.id}`;
};

export type ReservationFormData = Partial<Reservation> & {
  driver?: Partial<Driver>;
  vehicleId?: number;
  vehicle?: Partial<Vehicle>;
  startTime?: Date;
  endTime?: Date;
  pickupCountry?: number;
  pickupCity?: number;
  pickupPostalCode?: number;
  dropoffCountry?: number;
  dropoffCity?: number;
  dropoffPostalCode?: number;
}

export type FormRef = {
  submit: () => Promise<void>;
}

type Props = {
  formData: ReservationFormData;
  actionText?: string | React.ReactNode;
  setFormData: React.Dispatch<React.SetStateAction<ReservationFormData>>,
  handleSubmit: () => Promise<void>;
  lockedDriver?: Driver;
  lockedVehicle?: Vehicle;
  onValidationChange?: (hasErrors: boolean) => void;
  readOnly?: boolean;
}

const Form = forwardRef<FormRef, Props>(({
  formData,
  actionText,
  setFormData,
  handleSubmit,
  lockedDriver,
  lockedVehicle,
  onValidationChange,
  readOnly = false
}, ref) => {
  const { t } = useTranslation();

  const [formSchema, setFormSchema] = React.useState<FormSchema[]>([
    {
      name: 'pickup',
      label: t('Pickup'),
      icon: '📍',
      fields: [
        { name: 'pickupCountry', type: 'country-autocomplete', label: t('Country'), required: true, cols: 4, size: 'small' },
        { name: 'pickupCity', type: 'text', label: t('City'), required: true, cols: 4, size: 'small' },
        { name: 'pickupPostalCode', type: 'text', label: t('Postal code'), cols: 4, size: 'small' },
        {
          name: 'startTime', type: 'datetime', label: t('Time'), required: true, validationRules: [(value, formData) => {
            if (formData && new Date(value || '') >= new Date(formData.endTime || '')) {
              return t('Must be before end time');
            }
          }], cols: 12, size: 'small'
        },
      ]
    },
    {
      name: 'dropoff',
      label: t('Dropoff'),
      icon: '🚚',
      fields: [
        { name: 'dropoffCountry', type: 'country-autocomplete', label: t('Country'), required: true, cols: 4, size: 'small' },
        { name: 'dropoffCity', type: 'text', label: t('City'), required: true, cols: 4, size: 'small' },
        { name: 'dropoffPostalCode', type: 'text', label: t('Postal code'), cols: 4, size: 'small' },
        {
          name: 'endTime', type: 'datetime', label: t('Time'), cols: 12, required: true, validationRules: [(value, formData) => {
            if (formData && new Date(value || '') <= new Date(formData.startTime || '')) {
              return t('Must be after start time');
            }
          }], size: 'small'
        },
      ]
    },
    {
      name: 'additionalInfo',
      label: t('Additional information'),
      icon: '📝',
      fields: [
        {
          name: 'vehicle',
          type: 'async-select',
          label: t('Vehicle'),
          required: true,
          url: `${process.env.REACT_APP_API_URL}/api/v1/vehicles?limit=500&offset=0`,
          getOptionLabel: (option) => vehicleLabel(option as Vehicle & Record<string, unknown>),
          getOptionValue: (option: Vehicle) => option.id,
          cols: 6,
          disable: lockedVehicle !== undefined ? true : (formData.id ? true : false),
          size: 'small',
        },
        {
          name: 'driver',
          type: 'async-select',
          label: t('Driver'),
          required: true,
          url: `${process.env.REACT_APP_API_URL}/api/v1/drivers`,
          getOptionLabel: (option) => `${option.firstName} ${option.lastName}`,
          getOptionValue: (option: Driver) => option.id,
          cols: 6,
          disable: lockedDriver !== undefined ? true : (formData.id ? true : false),
          size: 'small',
        },
        { name: 'noDriverReason', type: 'select', label: t('No driver reason'), options: enumToOptions(NoDriverReason), cols: 6, size: 'small' },
      ]
    }
  ]);

  const formBuilderRef = React.useRef<FormBuilderRef>(null);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (formBuilderRef.current) {
        await formBuilderRef.current.submit();
      }
    }
  }));

  // If lockedDriver is set, ensure formData.driver is set to that value
  React.useEffect(() => {
    if (lockedDriver && (!formData.driver || formData.driver.id !== lockedDriver.id)) {
      setFormData(prev => ({
        ...prev,
        driver: lockedDriver,
        driverId: lockedDriver.id
      }));
    }
  }, [lockedDriver, formData.driver, setFormData]);

  React.useEffect(() => {
    if (lockedVehicle && (!formData.vehicle || formData.vehicle.id !== lockedVehicle.id)) {
      setFormData(prev => ({
        ...prev,
        vehicle: lockedVehicle,
        vehicleId: lockedVehicle.id
      }));
    }
  }, [lockedVehicle, formData.vehicle, setFormData]);

  return (
    <FormBuilder
      ref={formBuilderRef}
      formSchema={formSchema}
      formData={formData}
      setFormData={setFormData}
      handleSubmit={handleSubmit}
      actionText={actionText}
      onSchemaChange={setFormSchema}
      onValidationChange={onValidationChange}
      disabled={readOnly}
    />
  );
});

Form.displayName = 'Form';

export default Form;
